import { useEffect, useState } from "react";
import { TrendingUp, Star, ShieldOff, Calculator, BarChart3, MessageCircle } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const RISK_COLORS = {
  high: "bg-rose-500/20 border-rose-500/40 text-rose-300",
  medium: "bg-amber-500/20 border-amber-500/40 text-amber-300",
  low: "bg-blue-500/20 border-blue-500/40 text-blue-300",
  optimal: "bg-emerald-500/20 border-emerald-500/40 text-emerald-300",
};

export default function IntelligentSuite() {
  const [tab, setTab] = useState("yield");
  const [cars, setCars] = useState([]);
  const [yieldForm, setYieldForm] = useState({ car_id: "", target_date: new Date().toISOString().slice(0,10) });
  const [yieldResult, setYieldResult] = useState(null);
  const [analytics, setAnalytics] = useState([]);
  const [reviews, setReviews] = useState({ items: [], avg_rating: null, total: 0 });
  const [reviewForm, setReviewForm] = useState({ stars: 5, comment: "" });
  const [frozen, setFrozen] = useState([]);
  const [freezeForm, setFreezeForm] = useState({ car_id: "", reason: "" });

  const load = async () => {
    try {
      const [c, a, r, f] = await Promise.all([
        api.get("/cars"),
        api.get("/insurance-analytics/by-model"),
        api.get("/reviews"),
        api.get("/asset/frozen"),
      ]);
      setCars(c.data || []);
      setAnalytics(a.data?.analytics || []);
      setReviews(r.data);
      setFrozen(f.data?.items || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const calcYield = async () => {
    if (!yieldForm.car_id) { toast.error("Seleziona un'auto"); return; }
    try {
      const r = await api.post("/yield/calculate", yieldForm);
      setYieldResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const submitReview = async () => {
    try {
      const r = await api.post("/reviews/submit", reviewForm);
      toast.success(`Routing: ${r.data.routing.type}`);
      setReviewForm({ stars: 5, comment: "" });
      load();
    } catch (e) { toast.error("Errore"); }
  };

  const freezeAsset = async () => {
    if (!freezeForm.car_id || !freezeForm.reason) { toast.error("Completa i campi"); return; }
    try {
      const r = await api.post("/asset/remote-freeze", { ...freezeForm, notify_sms: true });
      toast.warning(r.data.warning, { duration: 8000 });
      setFreezeForm({ car_id: "", reason: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const unfreeze = async (car_id) => {
    if (!window.confirm("Sbloccare il veicolo?")) return;
    try { await api.post(`/asset/${car_id}/unfreeze`); toast.success("Veicolo sbloccato"); load(); }
    catch (e) { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6" data-testid="intelligent-suite-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight">Intelligent Business Suite</h1>
        <p className="text-sm text-white/45 mt-1">Dynamic Yield · Predictive Insurance · Reputation Engine · Remote Asset Freeze</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: "yield", label: "Dynamic Yield", icon: Calculator },
          { id: "insurance", label: "Insurance Analytics", icon: BarChart3 },
          { id: "reviews", label: `Reputation (${reviews.total})`, icon: Star },
          { id: "freeze", label: `Asset Freeze (${frozen.length})`, icon: ShieldOff },
        ].map((t) => {
          const Ic = t.icon;
          return (
            <button key={t.id} data-testid={`is-tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-[10px] sm:text-xs uppercase tracking-widest inline-flex items-center gap-2 ${tab === t.id ? "bg-[#ff0033] text-white" : "bg-white/5 text-white/60"}`}>
              <Ic size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "yield" && (
        <div className="space-y-4">
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
            <h3 className="text-xs uppercase tracking-widest text-white/60 mb-3 flex items-center gap-2"><TrendingUp size={14} className="text-[#ff0033]"/> Calcola tariffa dinamica</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <select data-testid="yield-car" value={yieldForm.car_id} onChange={(e)=>setYieldForm({...yieldForm, car_id: e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleziona auto</option>
                {cars.map(c => <option key={c.id} value={c.id}>{c.marca} {c.modello} · {c.targa} (€{c.prezzo_giornaliero}/gg)</option>)}
              </select>
              <input data-testid="yield-date" type="date" value={yieldForm.target_date} onChange={(e)=>setYieldForm({...yieldForm, target_date: e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <button data-testid="yield-calc" onClick={calcYield} className="btn-neon px-5 py-2 rounded-lg text-xs">Calcola</button>
            </div>
            {yieldResult && (
              <div className="mt-4 p-4 bg-black/30 rounded-xl">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40">Tariffa suggerita</div>
                    <div className="text-4xl font-black text-[#ff0033]">€{yieldResult.suggested_price}</div>
                    <div className="text-xs text-white/40">Base: €{yieldResult.base_price} · Moltiplicatore: ×{yieldResult.multiplier}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {Object.entries(yieldResult.breakdown).map(([k, v]) => (
                      <div key={k} className="bg-white/5 rounded px-2 py-1">
                        <span className="text-white/40 capitalize">{k.replace(/_/g, " ")}: </span>
                        <span className={v > 1 ? "text-emerald-300" : v < 1 ? "text-rose-300" : "text-white/80"}>×{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-3 text-[10px] text-white/30">Saturazione flotta: {(yieldResult.context.fleet_saturation * 100).toFixed(0)}% · Giorni di anticipo: {yieldResult.context.days_ahead} · {yieldResult.context.weekday}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "insurance" && (
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-black/40 text-white/60 text-[10px] uppercase tracking-widest">
              <tr><th className="text-left p-3">Modello</th><th className="text-left p-3">Auto</th><th className="text-right p-3">Costo Danni 12m</th><th className="text-right p-3">Costo Manut 12m</th><th className="text-right p-3">Cost Ratio</th><th className="text-left p-3">Rischio</th><th className="text-left p-3">Adjust Premio</th></tr>
            </thead>
            <tbody>
              {analytics.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-white/40 text-xs">Nessun dato</td></tr>}
              {analytics.map((a, i) => (
                <tr key={i} className="border-t border-white/5" data-testid={`ins-row-${i}`}>
                  <td className="p-3 font-medium">{a.model}</td>
                  <td className="p-3 text-xs">{a.cars}</td>
                  <td className="p-3 text-right font-mono">€{a.damage_cost_12m.toLocaleString()}</td>
                  <td className="p-3 text-right font-mono">€{a.maintenance_cost_12m.toLocaleString()}</td>
                  <td className="p-3 text-right font-mono">{(a.cost_ratio*100).toFixed(1)}%</td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase ${RISK_COLORS[a.insurance_risk]}`}>{a.insurance_risk}</span></td>
                  <td className="p-3 text-xs font-mono">{a.premium_adjust_suggestion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "reviews" && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
              <h3 className="text-xs uppercase tracking-widest text-white/60 mb-3">Test routing recensione</h3>
              <div className="flex items-center gap-2 mb-3">
                {[1,2,3,4,5].map((s) => (
                  <button key={s} onClick={() => setReviewForm({...reviewForm, stars: s})} className={`p-2 rounded-lg ${reviewForm.stars >= s ? "text-amber-300" : "text-white/20"}`}>
                    <Star size={24} fill={reviewForm.stars >= s ? "currentColor" : "none"} />
                  </button>
                ))}
                <span className="text-xs text-white/40 ml-2">{reviewForm.stars}/5</span>
              </div>
              <textarea data-testid="review-comment" placeholder="Commento opzionale..." value={reviewForm.comment} onChange={(e)=>setReviewForm({...reviewForm, comment: e.target.value})} rows={3} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <button data-testid="review-submit" onClick={submitReview} className="mt-3 btn-neon px-4 py-2 rounded-full text-xs">Simula submit</button>
              <div className="text-[10px] text-white/40 mt-3">5⭐ → Google Reviews · 4⭐ → Social · 1-3⭐ → Customer Care priority</div>
            </div>
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
              <h3 className="text-xs uppercase tracking-widest text-white/60 mb-3">Statistiche</h3>
              <div className="text-5xl font-black flex items-end gap-2">
                {reviews.avg_rating?.toFixed(1) || "—"}
                <Star size={28} className="text-amber-300 mb-1" fill="currentColor" />
              </div>
              <div className="text-xs text-white/40 mt-1">Media · {reviews.total} recensioni totali</div>
            </div>
          </div>
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-black/40 text-white/60 text-[10px] uppercase tracking-widest">
                <tr><th className="text-left p-3">Data</th><th className="text-left p-3">Stelle</th><th className="text-left p-3">Commento</th><th className="text-left p-3">Routing</th></tr>
              </thead>
              <tbody>
                {reviews.items?.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-white/40 text-xs">Nessuna recensione</td></tr>}
                {reviews.items?.map((r) => (
                  <tr key={r.id} className="border-t border-white/5">
                    <td className="p-3 text-[11px] text-white/60">{r.created_at?.slice(0,10)}</td>
                    <td className="p-3"><span className="text-amber-300">{"★".repeat(r.stars)}</span><span className="text-white/20">{"★".repeat(5-r.stars)}</span></td>
                    <td className="p-3 text-xs">{r.comment || "—"}</td>
                    <td className="p-3 text-[10px] uppercase">{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "freeze" && (
        <div className="space-y-4">
          <div className="bg-white/[0.02] border border-rose-500/40 rounded-2xl p-5">
            <h3 className="text-xs uppercase tracking-widest text-rose-300 mb-3 flex items-center gap-2"><ShieldOff size={14}/> Blocca veicolo da remoto</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <select data-testid="freeze-car" value={freezeForm.car_id} onChange={(e)=>setFreezeForm({...freezeForm, car_id: e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleziona auto</option>
                {cars.filter(c => !c.remote_freeze_active).map(c => <option key={c.id} value={c.id}>{c.marca} {c.modello} · {c.targa}</option>)}
              </select>
              <input data-testid="freeze-reason" placeholder="Motivo (es. mancato pagamento)" value={freezeForm.reason} onChange={(e)=>setFreezeForm({...freezeForm, reason: e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm md:col-span-2" />
            </div>
            <button data-testid="freeze-submit" onClick={freezeAsset} className="mt-3 px-4 py-2 rounded-full bg-rose-500/20 border border-rose-500/40 text-rose-300 text-xs">🔒 Blocca motore (audit + SMS mock)</button>
          </div>
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-black/40 text-white/60 text-[10px] uppercase tracking-widest">
                <tr><th className="text-left p-3">Targa</th><th className="text-left p-3">Veicolo</th><th className="text-left p-3">Motivo</th><th className="text-left p-3">Bloccato il</th><th className="text-right p-3"></th></tr>
              </thead>
              <tbody>
                {frozen.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-white/40 text-xs">Nessun veicolo bloccato</td></tr>}
                {frozen.map((f) => (
                  <tr key={f.id} className="border-t border-white/5" data-testid={`frozen-${f.id}`}>
                    <td className="p-3 font-mono text-xs">{f.targa}</td>
                    <td className="p-3 text-xs">{f.marca} {f.modello}</td>
                    <td className="p-3 text-xs text-rose-300">{f.remote_freeze_reason}</td>
                    <td className="p-3 text-[11px] text-white/60">{f.remote_freeze_at?.slice(0,19).replace("T"," ")}</td>
                    <td className="p-3 text-right"><button data-testid={`unfreeze-${f.id}`} onClick={() => unfreeze(f.id)} className="px-3 py-1 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[10px]">🔓 Sblocca</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
